import React, { useState } from "react";
import { Loader2, CreditCard, ShieldCheck, RefreshCw, XCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtCAD, fmtDate, STATUS_META, TONE_CLASSES, postToHosted } from "@/lib/billing";

export function StatusPill({ status, testid = "billing-status-pill" }) {
  const meta = STATUS_META[status] || { label: status, tone: "slate" };
  return (
    <span className={"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium " + TONE_CLASSES[meta.tone]} data-testid={testid}>
      {meta.label}
    </span>
  );
}

export default function PlanCard({ data, onChanged }) {
  const { status, plan, config } = data;
  const [busy, setBusy] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const s = status.status;

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error("Billing action failed", { description: formatApiError(e) }); } finally { setBusy(""); }
  };

  const subscribe = () => run("subscribe", async () => {
    const { data: res } = await api.post("/billing/checkout", { kind: "subscription" });
    if (res.mode === "charged") { toast.success("Subscription reactivated on your saved card"); onChanged?.(); return; }
    toast.message("Taking you to Authorize.Net's secure checkout…");
    postToHosted(res.hosted_url, res.token);
  });
  const updateCard = () => run("card", async () => {
    const { data: res } = await api.post("/billing/update-card");
    postToHosted(res.hosted_url, res.token);
  });
  const retry = () => run("retry", async () => { await api.post("/billing/retry"); toast.success("Payment went through — you're active again"); onChanged?.(); });
  const cancel = () => run("cancel", async () => { await api.post("/billing/cancel"); toast.success("Cancellation scheduled — you keep access until the period ends"); setConfirmCancel(false); onChanged?.(); });
  const resume = () => run("resume", async () => { await api.post("/billing/resume"); toast.success("Auto-renewal resumed"); onChanged?.(); });

  const quote = plan?.quote;
  const priceLine = quote
    ? `${fmtCAD(quote.total_cad)} / month (${fmtCAD(quote.subtotal_cad)} + ${fmtCAD(quote.tax_cad)} tax)`
    : `${fmtCAD(plan?.price_cad)} / month + applicable tax`;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="plan-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Plan</div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Realtor Outreach CRM — monthly</h2>
          <div className="text-sm text-slate-600 mt-0.5" data-testid="plan-price-line">{priceLine}</div>
        </div>
        <StatusPill status={s} />
      </div>

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Info label={s === "cancelling" ? "Access until" : s === "ended" ? "Ended on" : "Next renewal"} value={status.period_end ? fmtDate(status.period_end) : (s === "complimentary" ? fmtDate(status.complimentary_until) : "—")} testid="plan-next-renewal" />
        <Info label="Included monthly" value={`${plan?.included_enrichment?.toLocaleString()} enrich · ${plan?.included_scrape?.toLocaleString()} scrape`} />
        <Info label="Card on file" value={status.has_card ? `${status.card_brand || "Card"} •••• ${status.card_last4 || "????"}` : "None yet"} testid="plan-card-on-file" />
        <Info label="Billing province" value={status.billing_province ? `${status.billing_province} · from your location` : "Not set — ask an admin to assign your location"} testid="plan-province" />
      </dl>

      {s === "past_due" || s === "read_only" ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3" data-testid="plan-failure-note">
          <p className="text-xs text-amber-900">
            <span className="font-semibold">Your last payment didn't go through{status.last_failure ? ": " : "."}</span>
            {status.last_failure ? <span>{status.last_failure} </span> : null}
            {s === "past_due" ? `We'll retry automatically; access turns read-only in ${status.days_left ?? 0} day(s).` : "Your account is read-only until a payment succeeds."}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2" data-testid="plan-failure-actions">
            {status.has_card ? (
              <>
                <Button size="sm" onClick={updateCard} disabled={!!busy} className="text-white" style={{ background: "hsl(var(--brand))" }} data-testid="plan-failure-update-card">
                  {busy === "card" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5 mr-1.5" />} Update card
                </Button>
                <Button size="sm" variant="outline" onClick={retry} disabled={!!busy} data-testid="plan-failure-retry">
                  {busy === "retry" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Retry payment now
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={subscribe} disabled={!!busy} className="text-white" style={{ background: "hsl(var(--brand))" }} data-testid="plan-failure-add-card">
                {busy === "subscribe" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5 mr-1.5" />} Add a card to continue
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {s === "ended" ? (
        <p className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="plan-ended-note">
          Your subscription ended on {fmtDate(status.period_end)}. Your realtors, notes and history are safe — reactivate to pick up where you left off. Billing history and receipts stay available here.
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {s === "ended" && status.has_card ? (
          <Button onClick={retry} disabled={!!busy} className="text-white min-h-[44px]" style={{ background: "hsl(var(--brand))" }} data-testid="plan-reactivate-card">
            {busy === "retry" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Reactivate with {status.card_brand || "card"} •••• {status.card_last4 || "????"}
          </Button>
        ) : null}
        {(s === "inactive" || s === "ended" || s === "complimentary") ? (
          <Button onClick={subscribe} disabled={!!busy} variant={s === "ended" && status.has_card ? "outline" : "default"} className={s === "ended" && status.has_card ? "min-h-[44px]" : "text-white min-h-[44px]"} style={s === "ended" && status.has_card ? undefined : { background: "hsl(var(--brand))" }} data-testid="plan-subscribe">
            {busy === "subscribe" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {s === "complimentary" ? "Subscribe now" : s === "ended" ? (status.has_card ? "Reactivate with a different card" : `Reactivate for ${fmtCAD(plan?.price_cad)}/month + tax`) : `Activate for ${fmtCAD(plan?.price_cad)}/month + tax`}
          </Button>
        ) : null}
        {status.has_card && s !== "past_due" && s !== "read_only" ? (
          <Button variant="outline" onClick={updateCard} disabled={!!busy} className="min-h-[44px]" data-testid="plan-update-card">
            {busy === "card" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Update card
          </Button>
        ) : null}
        {s === "active" ? (
          <Button variant="ghost" onClick={() => setConfirmCancel(true)} disabled={!!busy} className="min-h-[44px] text-slate-600" data-testid="plan-cancel">
            <XCircle className="h-4 w-4 mr-2" /> Cancel at period end
          </Button>
        ) : null}
        {s === "cancelling" ? (
          <Button variant="outline" onClick={resume} disabled={!!busy} className="min-h-[44px]" data-testid="plan-resume">
            <Undo2 className="h-4 w-4 mr-2" /> Resume auto-renewal
          </Button>
        ) : null}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        Payments are taken on Authorize.Net's secure page — card details never touch this app. {config?.sandbox ? "Sandbox mode (test cards only)." : ""}
        {!config?.anet_configured ? " Online payments are not switched on yet." : ""}
      </p>
      <ConfirmDialog
        open={confirmCancel}
        title="Cancel your subscription?"
        description={`You keep full access until ${fmtDate(status.period_end)}. No further charges after that; your data stays put.`}
        confirmLabel="Cancel at period end"
        destructive
        onConfirm={cancel}
        onClose={() => setConfirmCancel(false)}
      />
    </section>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-900 mt-0.5" data-testid={testid}>{value}</dd>
    </div>
  );
}
